import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import TableLightBackground from '../../../Components/TableLightBackground/TableLightBackground';
import { useDeleteThresholdFormMutation, useGetInspectionFormsQuery } from '../../../redux/rtkQuery/apiFormbuilderSlice';
import { RootState } from '../../../redux/root';
import { setForms, setSelectedForm, setThresholdDeleteCandidate } from '../../../redux/slices/thresholdFormSlice';
import GenerateTemplatePopup from '../../../organisms/ThresholdReports/PreviewTool/GenerateTemplatePopup';
import { ModalDefaultDraggable } from '../../../molecules';
import AddNewForm from './AddNewForm';
import { getInspectionTypes } from '../../../api/inspectionTypes';

// Form Name / Description / Inspection Type-Jurisdiction. The last column is a
// bare "View" link — the full list runs to a dozen-plus entries per form, so it
// belongs in a popup rather than stacked in the cell.
const ROWS_TEMPLATE = '280px 1fr 200px';

const ALL_TYPES_LABEL = 'All inspection types';

type TypeGroup = { jurisdictions: string[]; types: string[] };

// `jurisdictionNames` comes from Jurisdiction.__str__, which prefixes every name
// with "Jurisdiction | ". Prefer the clean `jurisdictionsDetail` names and strip
// the prefix when falling back to the older field.
const cleanJurisdictionNames = (form: any): string[] => {
  const detailNames = form.jurisdictionsDetail?.map((jur: any) => jur?.name).filter(Boolean);
  const jurisdictions = detailNames?.length > 0 ? detailNames : form.jurisdictionNames;
  if (!Array.isArray(jurisdictions)) return [];

  return Array.from(new Set<string>(jurisdictions))
    .map((name: string) => name.replace(/^Jurisdiction\s*\|\s*/, ''))
    .sort((a, b) => a.localeCompare(b));
};

// One entry per distinct set of inspection types, listing every jurisdiction
// that carries exactly that set.
//
// A form is assigned either to specific inspection types or to whole
// jurisdictions ("by Jurisdiction" in AddNewForm). Each InspectionType belongs
// to exactly one jurisdiction and the same type name exists once per
// jurisdiction, so a bare name list ("T001 Threshold Concrete" fourteen times)
// tells the admin nothing about which jurisdiction the form covers. Merging by
// type set keeps the pairing without repeating a near-identical line per
// jurisdiction — where the sets genuinely differ the entries split, e.g.
// "Collier County — T001, T002" / "Lee County — T001".
const buildTypeGroups = (form: any, activeTypeIds?: Set<number>): TypeGroup[] => {
  const detail = form.inspectionTypesDetail;

  if (Array.isArray(detail) && detail.length > 0) {
    const activeDetail = activeTypeIds
      ? detail.filter((type: any) => activeTypeIds.has(type?.id))
      : detail;
    const byJurisdiction = new Map<string, Set<string>>();
    activeDetail.forEach((type: any) => {
      const jurisdiction = type?.jurisdictionName || 'No jurisdiction';
      if (!byJurisdiction.has(jurisdiction)) {
        byJurisdiction.set(jurisdiction, new Set());
      }
      if (type?.name) {
        byJurisdiction.get(jurisdiction).add(type.name);
      }
    });

    const byTypeSet = new Map<string, TypeGroup>();
    Array.from(byJurisdiction.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([jurisdiction, names]) => {
        const types = Array.from(names).sort((a, b) => a.localeCompare(b));
        const key = types.join(' ');
        if (!byTypeSet.has(key)) {
          byTypeSet.set(key, { jurisdictions: [], types });
        }
        byTypeSet.get(key).jurisdictions.push(jurisdiction);
      });

    return Array.from(byTypeSet.values()).sort((a, b) => a.jurisdictions[0].localeCompare(b.jurisdictions[0]));
  }

  // Backend without `inspection_types_detail` (older API): the jurisdiction is
  // simply not in the payload, so fall back to the bare deduped names.
  if (form.inspectionTypes?.length > 0) {
    const names = form.inspectionTypesNames;
    return Array.isArray(names) ? [{ jurisdictions: [], types: Array.from(new Set<string>(names)) }] : [];
  }

  // Assigned by jurisdiction — every type in each one is covered, so they all
  // share the same "set" and collapse into a single entry.
  const jurisdictions = cleanJurisdictionNames(form);
  return jurisdictions.length > 0 ? [{ jurisdictions, types: [] }] : [];
};

const FormsTable = () => {
  const { data } = useGetInspectionFormsQuery({ limit: 1000 });
  const [deleteThresholdForm] = useDeleteThresholdFormMutation();
  const [showConfig, setShowConfig] = useState<boolean>(false);
  const [showEditForm, setShowEditForm] = useState<boolean>(false);
  const { forms, deleteCandidate } = useSelector((s: RootState) => s.thresholdForms);
  const [selectedRow, setSelectedRow] = useState();
  const [detailsModal, setDetailsModal] = useState<any>(null);
  const [activeTypeIds, setActiveTypeIds] = useState<Set<number>>(new Set());

  const dispatch = useDispatch();

  useEffect(() => {
    const loadActiveTypes = async () => {
      const results = await getInspectionTypes({ is_deleted: false, is_threshold: true, limit: 1000000 });
      const ids = new Set(results.data.results.filter(t => t.isActive).map(t => t.id));
      setActiveTypeIds(ids);
    };
    loadActiveTypes();
  }, []);

  useEffect(() => {
    if (data && activeTypeIds.size > 0) {
      const results = data.results.map(res => {
        const typeGroups = buildTypeGroups(res, activeTypeIds);
        return {
          ...res,
          typeGroups,
          typesView: typeGroups.length > 0 ? 'View' : '',
        };
      });
      dispatch(setForms(results));
    }
  }, [data, activeTypeIds]);
  const headers = [
    { title: 'Form Name', key: 'name' },
    { title: 'Description', key: 'description' },
    { title: 'Inspection Type/Jurisdiction', key: 'typesView', clickable: true },
  ];

  const onEditForm = row => {
    setSelectedRow(row);
    setShowEditForm(true);
  };

  const onDelete = async () => {
    await deleteThresholdForm({ id: deleteCandidate.id });
  };

  const updateDeleteRecordCandidate = row => {
    dispatch(setThresholdDeleteCandidate(row));
  };

  const onControlsDetails = row => {
    dispatch(setSelectedForm(row));
    setShowConfig(true);
  };

  return (
    <div>
      <GenerateTemplatePopup show={showConfig} setShow={setShowConfig} />
      {showEditForm && (
        <ModalDefaultDraggable title="Edit Manual Form" onClose={() => setShowEditForm(false)}>
          <AddNewForm initialValues={selectedRow} selectedFontSize={16} setShowAdd={setShowEditForm} />
        </ModalDefaultDraggable>
      )}
      <TableLightBackground
        onEdit={onEditForm}
        onImageDeleted={onDelete}
        onPreview={() => {}}
        rowsTemplate={ROWS_TEMPLATE}
        headers={headers}
        rows={forms}
        deleteConfirmation="Are you sure you want to delete the Manual Form Record?"
        onOK={onDelete}
        onCancel={() => {}}
        onDetails={onControlsDetails}
        setDeleteRecordCandidate={updateDeleteRecordCandidate}
        onCellClick={row => setDetailsModal(row)}
      />
      {detailsModal && (
        <ModalDefaultDraggable
          title={`Inspection Types — ${detailsModal.name || ''}`}
          onClose={() => setDetailsModal(null)}
          // Not `narrow` (480px): the shared StyledTitle is centered at 1.5rem
          // bold on 90% of the content box, so "Inspection Types — <form name>"
          // wraps to two lines in that shell. 640px keeps it on one line and
          // gives the jurisdiction lists room to breathe.
          maxWidth="640px"
        >
          <DetailsList>
            {(detailsModal.typeGroups || []).map((group: TypeGroup, index: number) => (
              <li key={index}>
                <PrimaryLine>{group.types.length > 0 ? group.types.join(', ') : ALL_TYPES_LABEL}</PrimaryLine>
                {group.jurisdictions.length > 0 && <SecondaryLine>{group.jurisdictions.join(', ')}</SecondaryLine>}
              </li>
            ))}
          </DetailsList>
        </ModalDefaultDraggable>
      )}
    </div>
  );
};

const DetailsList = styled.ul`
  list-style: disc;
  margin: 0;
  padding: 4px 8px 4px 24px;
  font-size: 0.875rem;
  color: var(--mainText);

  li {
    margin-bottom: 12px;
    break-inside: avoid;
  }
`;

const PrimaryLine = styled.div`
  font-weight: 600;
`;

const SecondaryLine = styled.div`
  color: var(--neutral-500);
  font-size: 0.8125rem;
`;

export default FormsTable;
